import { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { adminSidebar } from '@/config/sidebarConfig';
import { disableCompetitionApi, enableCompetitionApi, getCompetitionsApi } from '@/lib/api/competitionsApi';
import { getOrganizationsApi } from '@/lib/api/organizationsApi';
import { getAdminStatsApi } from '@/lib/api/usersApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { COMP_STATUS_CONFIG } from '@/lib/status-config';
import { useAuthStore } from '@/stores/auth-store';
import {
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateAbbreviatedFr,
  formatEur,
} from '@shared';
import { CompetitionStatus, CompetitionType } from '@shared/enums/domain.enum';
import type { Competition, GetCompetitionsParams } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  MapPin,
  Pencil,
  RotateCcw,
  Search,
  Trophy,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Constants ──────────────────────────────────────────────────

const PAGE_SIZE = 25;

// ─── Config ──────────────────────────────────────────────────────

type SortKey = 'date_desc' | 'date_asc' | 'name_asc' | 'name_desc' | 'created_desc';

const SORT_OPTIONS: { value: SortKey; label: string; sortBy: string; sortOrder: 'asc' | 'desc' }[] = [
  { value: 'date_desc', label: 'Date — récente', sortBy: 'date', sortOrder: 'desc' },
  { value: 'date_asc', label: 'Date — ancienne', sortBy: 'date', sortOrder: 'asc' },
  { value: 'name_asc', label: 'Nom A → Z', sortBy: 'name', sortOrder: 'asc' },
  { value: 'name_desc', label: 'Nom Z → A', sortBy: 'name', sortOrder: 'desc' },
  { value: 'created_desc', label: 'Créé récemment', sortBy: 'createdAt', sortOrder: 'desc' },
];

const STATUS_TABS: { value: CompetitionStatus | ''; label: string; key: string }[] = [
  { value: '', label: 'Tous', key: 'all' },
  { value: CompetitionStatus.DRAFT, label: 'Brouillons', key: 'DRAFT' },
  { value: CompetitionStatus.REGISTRATION_OPEN, label: 'Inscriptions', key: 'REGISTRATION_OPEN' },
  { value: CompetitionStatus.IN_PROGRESS, label: 'En cours', key: 'IN_PROGRESS' },
  { value: CompetitionStatus.FINISHED, label: 'Terminés', key: 'FINISHED' },
  { value: CompetitionStatus.CANCELLED, label: 'Annulés', key: 'CANCELLED' },
  { value: CompetitionStatus.DISABLED_BY_ADMIN, label: 'Désactivés', key: 'DISABLED_BY_ADMIN' },
];

const VALID_STATUSES = new Set<string>(Object.values(CompetitionStatus));
const VALID_TYPES = new Set<string>(Object.values(CompetitionType));
const VALID_SORTS = new Set<string>(SORT_OPTIONS.map(o => o.value));

// ─── Helpers ─────────────────────────────────────────────────────

function relativeDate(dateStr: string): string {
  const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Demain';
  if (diff > 1 && diff <= 60) return `Dans ${diff} j.`;
  if (diff < 0 && diff >= -60) return `Il y a ${-diff} j.`;
  return '';
}

function fillColor(fill: number): string {
  if (fill >= 100) return 'bg-red-500';
  if (fill >= 80) return 'bg-amber-400';
  return 'bg-emerald-500';
}

// ─── URL param helpers ─────────────────────────────────────────────────────

function applyParamUpdates(prev: URLSearchParams, updates: Record<string, string | undefined>): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
  }
  return next;
}

// ─── Competition row ─────────────────────────────────────────────

interface CompRowProps {
  comp: Competition;
  orgName: string;
  onEdit: () => void;
  onDisable: () => void;
  onEnable: () => void;
  enabling: boolean;
}

function CompRow({ comp, orgName, onEdit, onDisable, onEnable, enabling }: CompRowProps) {
  const statusCfg = COMP_STATUS_CONFIG[comp.status];
  const isDisabled = comp.status === CompetitionStatus.DISABLED_BY_ADMIN;
  const fill = comp.maxTeams ? Math.round((comp.currentTeams / comp.maxTeams) * 100) : 0;
  const rel = comp.date ? relativeDate(comp.date) : '';

  return (
    <div className='grid grid-cols-12 gap-3 px-6 py-3.5 border-b border-slate-50 items-center hover:bg-slate-50/70 transition group'>
      {/* Concours */}
      <div className='col-span-3 min-w-0'>
        <p className='text-sm font-semibold text-slate-900 truncate'>{comp.name}</p>
        <div className='flex items-center gap-2 mt-0.5'>
          <span className='text-[10px] font-mono text-slate-400 truncate'>{comp.alias}</span>
          {orgName && <span className='text-[10px] text-slate-500 truncate'>· {orgName}</span>}
        </div>
      </div>

      {/* Date */}
      <div className='col-span-2'>
        <div className='flex items-center gap-1.5'>
          <Calendar className='w-3.5 h-3.5 text-slate-400 shrink-0' />
          <span className='text-sm text-slate-700'>{comp.date ? formatDateAbbreviatedFr(comp.date) : '—'}</span>
        </div>
        {rel && <p className='text-xs text-slate-400 mt-0.5 pl-5'>{rel}</p>}
      </div>

      {/* Type / Niveau */}
      <div className='col-span-2 flex flex-col gap-1'>
        <span className='inline-flex w-fit px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold'>
          {comp.type ? (TYPE_LABELS[comp.type] ?? comp.type) : '—'}
        </span>
        <span className='inline-flex w-fit px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs'>
          {comp.level ? (LEVEL_LABELS[comp.level] ?? comp.level) : '—'}
        </span>
      </div>

      {/* Places */}
      <div className='col-span-2'>
        {comp.maxTeams ? (
          <>
            <div className='flex items-center gap-1.5 mb-1'>
              <Users className='w-3.5 h-3.5 text-slate-400 shrink-0' />
              <span className='text-sm text-slate-700 font-medium'>
                {comp.currentTeams}
                <span className='text-slate-400 font-normal'>/{comp.maxTeams}</span>
              </span>
            </div>
            <div className='h-1.5 bg-slate-100 rounded-full overflow-hidden'>
              <div
                className={`h-full rounded-full ${fillColor(fill)} transition-all`}
                style={{ width: `${Math.min(fill, 100)}%` }}
              />
            </div>
          </>
        ) : (
          <span className='text-xs text-slate-400'>—</span>
        )}
      </div>

      {/* Prix */}
      <div className='col-span-1 text-right'>
        {comp.price && comp.price > 0 ? (
          <span className='text-sm font-semibold text-slate-900'>{formatEur(comp.price)}</span>
        ) : (
          <span className='text-xs font-semibold text-emerald-600'>Gratuit</span>
        )}
      </div>

      {/* Statut + Action */}
      <div className='col-span-2 flex items-center justify-end gap-2'>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}>
          {statusCfg.label}
        </span>
        <button
          onClick={onEdit}
          className='p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
          aria-label='Modifier le concours'
        >
          <Pencil className='w-3.5 h-3.5' />
        </button>
        {!isDisabled ? (
          <button
            onClick={onDisable}
            className='p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
            aria-label='Désactiver le concours'
            title='Désactiver'
          >
            <Ban className='w-3.5 h-3.5' />
          </button>
        ) : (
          <button
            onClick={onEnable}
            disabled={enabling}
            className='p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition disabled:opacity-50'
            aria-label='Réactiver le concours'
            title='Réactiver (recalcule le statut)'
          >
            <RotateCcw className='w-3.5 h-3.5' />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Disable confirmation modal (récap + irréversible) ───────────

interface DisableModalProps {
  comp: Competition;
  orgName: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function DisableCompetitionModal({ comp, orgName, isLoading, onConfirm, onCancel }: DisableModalProps) {
  const statusCfg = COMP_STATUS_CONFIG[comp.status];
  const recap: { label: string; value: string }[] = [
    { label: 'Organisateur', value: orgName || `#${comp.organizationId}` },
    { label: 'Alias', value: comp.alias },
    { label: 'Date', value: comp.date ? formatDateAbbreviatedFr(comp.date) : '—' },
    {
      label: 'Type / Niveau',
      value:
        [
          comp.type ? (TYPE_LABELS[comp.type] ?? comp.type) : null,
          comp.level ? (LEVEL_LABELS[comp.level] ?? comp.level) : null,
        ]
          .filter(Boolean)
          .join(' · ') || '—',
    },
    { label: 'Lieu', value: [comp.city, comp.department].filter(Boolean).join(', ') || '—' },
    {
      label: 'Inscriptions',
      value: comp.maxTeams ? `${comp.currentTeams} / ${comp.maxTeams}` : String(comp.currentTeams),
    },
    { label: 'Prix', value: comp.price && comp.price > 0 ? formatEur(comp.price) : 'Gratuit' },
    { label: 'Statut actuel', value: statusCfg.label },
  ];

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={isLoading ? undefined : onCancel} />

      <div className='relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6'>
        <div className='flex items-start gap-4 mb-4'>
          <div className='w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5'>
            <AlertTriangle className='w-5 h-5 text-red-600' />
          </div>
          <div className='flex-1 min-w-0'>
            <h2 className='text-base font-bold text-slate-900'>Désactiver ce concours&nbsp;?</h2>
            <p className='text-sm text-slate-500 mt-1 leading-relaxed'>
              Le concours sera retiré de la visibilité publique (recherche, carte, compteurs). L'organisateur le verra
              avec le badge «&nbsp;Désactivé&nbsp;». Réversible via le bouton «&nbsp;Réactiver&nbsp;» de l'onglet
              Désactivés.
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className='p-1 rounded-lg hover:bg-slate-100 transition-colors shrink-0 disabled:opacity-50 cursor-pointer'
          >
            <X className='w-4 h-4 text-slate-400' />
          </button>
        </div>

        {/* Récap */}
        <div className='rounded-xl border border-slate-200 bg-slate-50/70 p-4'>
          <div className='flex items-center gap-2 mb-3'>
            <Trophy className='w-4 h-4 text-slate-400 shrink-0' />
            <p className='text-sm font-bold text-slate-900 truncate'>{comp.name}</p>
          </div>
          <dl className='grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2'>
            {recap.map(r => (
              <div
                key={r.label}
                className='flex items-center justify-between gap-2 text-xs sm:flex-col sm:items-start sm:gap-0.5'
              >
                <dt className='text-slate-400 font-medium uppercase tracking-wide sm:flex sm:items-center sm:gap-1'>
                  {r.label === 'Lieu' && <MapPin className='w-3 h-3' />}
                  {r.label}
                </dt>
                <dd className='text-slate-800 font-semibold text-right sm:text-left'>{r.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className='flex gap-3 justify-end mt-6'>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className='px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50 cursor-pointer'
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className='inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 cursor-pointer'
          >
            <Ban className='w-4 h-4' />
            {isLoading ? 'Désactivation…' : 'Désactiver définitivement'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────

export default function AdminCompetitions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();
  const [compToDisable, setCompToDisable] = useState<Competition | null>(null);

  // ─── Read filters from URL ───
  const urlStatus = searchParams.get('status') ?? '';
  const urlType = searchParams.get('type') ?? '';
  const urlOrg = searchParams.get('org') ?? '';
  const urlSort = searchParams.get('sort') ?? 'date_desc';
  const urlPage = Math.max(1, Number(searchParams.get('page') ?? '1'));

  const statusFilter = (VALID_STATUSES.has(urlStatus) ? urlStatus : '') as CompetitionStatus | '';
  const typeFilter = (VALID_TYPES.has(urlType) ? urlType : '') as CompetitionType | '';
  const orgFilter = urlOrg ? Number(urlOrg) || '' : ('' as number | '');
  const sortKey = (VALID_SORTS.has(urlSort) ? urlSort : 'date_desc') as SortKey;
  const page = urlPage;

  // ─── searchInput is local (debounce buffer); search is URL-persisted ───
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const search = searchParams.get('search') ?? '';

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(prev => applyParamUpdates(prev, updates), { replace: true });
  }

  const sortOpt = SORT_OPTIONS.find(o => o.value === sortKey) ?? SORT_OPTIONS[0];

  // ─── Search debounce ───
  function handleSearchInput(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ search: value.trim() || undefined, page: undefined });
    }, 350);
  }

  function handleTabClick(value: CompetitionStatus | '') {
    updateParams({ status: value || undefined, page: undefined });
  }

  function handleClearFilters() {
    setSearchInput('');
    updateParams({ search: undefined, type: undefined, org: undefined, page: undefined });
  }

  // ─── Platform stats (AdminStats cache) ───
  const { data: platformStats } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: getAdminStatsApi,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  });

  // ─── Organisations dropdown ───
  const { data: orgs = [] } = useQuery({
    queryKey: ['admin', 'all-orgs-names'],
    queryFn: async () => {
      const limit = 100;
      let p = 1;
      const all: { id: number; name: string }[] = [];
      while (true) {
        const res = await getOrganizationsApi({ limit, page: p, sortBy: 'name', sortOrder: 'asc' });
        all.push(...res.data.map(o => ({ id: o.id, name: o.name })));
        if (p >= res.meta.totalPages) break;
        p++;
      }
      return all;
    },
    staleTime: 10 * 60 * 1000,
    enabled: isAuthenticated,
  });
  const orgMap = useMemo(() => new Map(orgs.map(o => [o.id, o.name])), [orgs]);

  // ─── Competitions list ───
  const listParams: GetCompetitionsParams = {
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(typeFilter && { type: typeFilter }),
    ...(orgFilter && { organizationId: Number(orgFilter) }),
    sortBy: sortOpt.sortBy,
    sortOrder: sortOpt.sortOrder,
    page,
    limit: PAGE_SIZE,
  };

  const {
    data: listData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.competitions.list(listParams as Record<string, unknown>),
    queryFn: () => getCompetitionsApi(listParams),
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
  });

  // ─── Disable competition (super-admin, irréversible) ───
  const disableMutation = useMutation({
    mutationFn: (id: number) => disableCompetitionApi(id),
    onSuccess: () => {
      toast.success('Concours désactivé');
      setCompToDisable(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erreur lors de la désactivation');
    },
  });

  // ─── Enable competition (super-admin, réactivation) ───
  const enableMutation = useMutation({
    mutationFn: (id: number) => enableCompetitionApi(id),
    onSuccess: () => {
      toast.success('Concours réactivé');
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erreur lors de la réactivation');
    },
  });

  const competitions: Competition[] = listData?.data ?? [];
  const total = listData?.meta?.total ?? 0;
  const totalPages = listData?.meta?.totalPages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const byStatus = platformStats?.competitions.byStatus ?? {};
  const hasFilters = search !== '' || typeFilter !== '' || orgFilter !== '';

  const isPageLoading = isLoading || !isAuthenticated;

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8 space-y-6'>
        <PageHeader title='Concours' subtitle='Tous les concours de la plateforme' />

        {/* ── KPI cards ── */}
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          <StatCard
            icon={Trophy}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Total concours'
            value={(platformStats?.competitions.total ?? 0).toLocaleString('fr-FR')}
            subtitle={`${platformStats?.competitions.thisYear ?? 0} cette année`}
          />
          <StatCard
            icon={Zap}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Inscriptions ouvertes'
            value={(
              (byStatus[CompetitionStatus.REGISTRATION_OPEN] ?? 0) + (byStatus[CompetitionStatus.IN_PROGRESS] ?? 0)
            ).toLocaleString('fr-FR')}
            subtitle={`${byStatus[CompetitionStatus.IN_PROGRESS] ?? 0} en cours`}
          />
          <StatCard
            icon={Clock}
            iconBg='bg-amber-100'
            iconColor='text-amber-600'
            label='À venir'
            value={(platformStats?.competitions.upcoming ?? 0).toLocaleString('fr-FR')}
            subtitle={`${byStatus[CompetitionStatus.DRAFT] ?? 0} brouillon(s)`}
          />
          <StatCard
            icon={CheckCircle}
            iconBg='bg-slate-100'
            iconColor='text-slate-600'
            label='Terminés'
            value={(platformStats?.competitions.finished ?? 0).toLocaleString('fr-FR')}
            subtitle={`${byStatus[CompetitionStatus.CANCELLED] ?? 0} annulé(s)`}
          />
        </div>

        {isError && <QueryErrorState onRetry={() => void refetch()} />}

        {/* ── Table ── */}
        <div className='bg-white rounded-2xl border border-slate-200'>
          {/* Status tabs */}
          <div className='border-b border-slate-200 px-6 pt-4 flex items-center gap-1 overflow-hidden'>
            {STATUS_TABS.map(tab => {
              const count = tab.value === '' ? platformStats?.competitions.total : byStatus[tab.value];
              const active = statusFilter === tab.value;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab.value)}
                  className={`shrink-0 px-4 py-2.5 text-sm font-semibold border-b-2 transition -mb-px cursor-pointer ${
                    active ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {tab.label}
                  {count != null && (
                    <span
                      className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                        active ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {count.toLocaleString('fr-FR')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Filters bar */}
          <div className='flex items-center justify-between flex-wrap gap-3 px-6 py-3 border-b border-slate-100'>
            {/* Search */}
            <div className='relative'>
              <Search className='w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2' />
              <input
                type='text'
                value={searchInput}
                onChange={e => handleSearchInput(e.target.value)}
                placeholder='Nom, alias…'
                className='pl-9 pr-4 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm w-52'
              />
            </div>

            <div className='flex items-center gap-2 flex-wrap'>
              {/* Type */}
              <select
                value={typeFilter}
                onChange={e => updateParams({ type: e.target.value || undefined, page: undefined })}
                className='px-3 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm bg-white'
              >
                <option value=''>Tous les types</option>
                {Object.entries(TYPE_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>

              {/* Organisation */}
              <select
                value={orgFilter}
                onChange={e => updateParams({ org: e.target.value || undefined, page: undefined })}
                className='px-3 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm bg-white max-w-[180px]'
              >
                <option value=''>Toutes les orgas</option>
                {orgs.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortKey}
                onChange={e => updateParams({ sort: e.target.value, page: undefined })}
                className='px-3 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm bg-white'
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              {hasFilters && (
                <button
                  onClick={handleClearFilters}
                  className='px-3 py-2 text-xs font-semibold text-slate-500 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50 transition'
                >
                  Effacer
                </button>
              )}

              {/* Result count + fetching indicator */}
              <div className='flex items-center gap-1.5 text-xs text-slate-400'>
                {isFetching && !isPageLoading && <span className='animate-pulse'>Actualisation…</span>}
                {!isFetching && total > 0 && (
                  <span className='px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-semibold'>
                    {total.toLocaleString('fr-FR')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Table header */}
          <div className='hidden lg:grid lg:grid-cols-12 gap-3 px-6 py-2.5 border-b border-slate-100 text-xs font-semibold text-slate-400 uppercase tracking-wider'>
            <div className='col-span-3'>Concours</div>
            <div className='col-span-2'>Date</div>
            <div className='col-span-2'>Type / Niveau</div>
            <div className='col-span-2'>Places</div>
            <div className='col-span-1 text-right'>Prix</div>
            <div className='col-span-2 text-right'>Statut</div>
          </div>

          {/* Table body */}
          {isPageLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className='grid grid-cols-12 gap-3 px-6 py-4 border-b border-slate-50'>
                {[3, 2, 2, 2, 1, 2].map((span, j) => (
                  <div key={j} className={`col-span-${span} h-4 animate-pulse rounded bg-slate-200`} />
                ))}
              </div>
            ))
          ) : competitions.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
              <FileText className='w-10 h-10 opacity-30' />
              <p className='text-sm font-medium'>
                {hasFilters || statusFilter ? 'Aucun concours avec ces filtres' : "Aucun concours pour l'instant"}
              </p>
            </div>
          ) : (
            competitions.map(comp => (
              <CompRow
                key={comp.id}
                comp={comp}
                orgName={orgMap.get(comp.organizationId) ?? ''}
                onEdit={() => navigate(ROUTES.COMPETITION_EDIT.replace(':id', String(comp.id)))}
                onDisable={() => setCompToDisable(comp)}
                onEnable={() => enableMutation.mutate(comp.id)}
                enabling={enableMutation.isPending && enableMutation.variables === comp.id}
              />
            ))
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
              <p className='text-sm text-slate-500'>
                <span className='font-semibold text-slate-700'>{from}</span>
                {' – '}
                <span className='font-semibold text-slate-700'>{to}</span>
                {' sur '}
                <span className='font-semibold text-slate-700'>{total.toLocaleString('fr-FR')}</span>
              </p>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => updateParams({ page: String(Math.max(1, page - 1)) })}
                    disabled={page === 1}
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  {getPageNumbers(page, totalPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className='px-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => updateParams({ page: String(p) })}
                        className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                          p === page ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => updateParams({ page: String(Math.min(totalPages, page + 1)) })}
                    disabled={page >= totalPages}
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {compToDisable && (
        <DisableCompetitionModal
          comp={compToDisable}
          orgName={orgMap.get(compToDisable.organizationId) ?? ''}
          isLoading={disableMutation.isPending}
          onConfirm={() => disableMutation.mutate(compToDisable.id)}
          onCancel={() => setCompToDisable(null)}
        />
      )}
    </DashboardLayout>
  );
}
